import { hasInfiniteCredits } from '@/lib/subscription-utils'
import { AnimatePresence, motion } from 'framer-motion'
import Image from 'next/image'
import { memo, useCallback, useEffect, useRef, useState } from 'react'
import type { ApiUserQuota } from 'sprites-types/api/user-quota'
import type { ApiUser } from 'sprites-types/api/users'
import { SidebarProfileDropdown } from './SidebarProfileDropdown'

// Hook to animate number counting with proper cleanup and edge case handling
const useCountAnimation = (targetValue: number | null | undefined, duration: number = 800) => {
  const [displayValue, setDisplayValue] = useState(targetValue ?? 0)
  const rafIdRef = useRef<number | null>(null)

  useEffect(() => {
    // Handle null/undefined/non-finite values
    if (targetValue == null || !Number.isFinite(targetValue)) {
      setDisplayValue(targetValue ?? 0)
      return
    }

    // If target hasn't changed significantly, don't animate
    if (targetValue === displayValue) {
      return
    }

    const startValue = displayValue
    const startTime = performance.now()

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime
      const progress = Math.min(elapsed / duration, 1)

      // Ease-out cubic function for smooth deceleration
      const easeOutCubic = 1 - Math.pow(1 - progress, 3)
      const currentValue = Math.round(startValue + (targetValue - startValue) * easeOutCubic)

      setDisplayValue(currentValue)

      if (progress < 1) {
        rafIdRef.current = requestAnimationFrame(animate)
      } else {
        rafIdRef.current = null
      }
    }

    rafIdRef.current = requestAnimationFrame(animate)

    // Cleanup function to cancel animation if component unmounts or targetValue changes
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current)
        rafIdRef.current = null
      }
    }
  }, [targetValue, duration])

  return displayValue
}

type SidebarFooterProps = {
  user: ApiUser | null
  quota: ApiUserQuota | null
  isCollapsed: boolean
  onOpenIntegrations: () => void
  onDropdownOpenChange?: (isOpen: boolean) => void
  onDropdownHoverChange?: (isHovering: boolean) => void
  shouldCloseDropdown?: boolean
}

export const SidebarFooter = memo(function SidebarFooter({
  user,
  quota,
  isCollapsed,
  onOpenIntegrations,
  onDropdownOpenChange,
  onDropdownHoverChange,
  shouldCloseDropdown,
}: SidebarFooterProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Animate credit count changes
  const creditsValue = typeof quota?.credits === 'number' ? quota.credits : null
  const animatedCredits = useCountAnimation(creditsValue)

  // Notify parent component when dropdown state changes
  const handleDropdownOpenChange = useCallback(
    (isOpen: boolean) => {
      setIsDropdownOpen(isOpen)
      onDropdownOpenChange?.(isOpen)
      // When dropdown closes, also reset hover state
      if (!isOpen) {
        onDropdownHoverChange?.(false)
      }
    },
    [onDropdownOpenChange, onDropdownHoverChange]
  )

  // Close dropdown when parent requests it (via shouldCloseDropdown)
  useEffect(() => {
    if (shouldCloseDropdown && isDropdownOpen) {
      handleDropdownOpenChange(false)
    }
  }, [shouldCloseDropdown, isDropdownOpen, handleDropdownOpenChange])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        handleDropdownOpenChange(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [handleDropdownOpenChange])

  if (!user) return null

  const userInitial = user.name
    ? user.name.charAt(0).toUpperCase()
    : user.email!.charAt(0).toUpperCase()

  if (isCollapsed) {
    return (
      <>
        <div className="border-t border-gray-200/50 p-3">
          <button
            ref={buttonRef}
            onClick={() => handleDropdownOpenChange(!isDropdownOpen)}
            className="mx-auto flex h-10 w-10 items-center justify-center overflow-hidden rounded-full border border-gray-200 transition-colors hover:border-gray-400"
            title={user.name || user.email || 'Profile'}
          >
            {user.image ? (
              <Image
                src={user.image}
                alt={user.name || user.email || 'User'}
                width={40}
                height={40}
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#48039E] to-[#7F039E] text-sm font-medium text-white">
                {userInitial}
              </div>
            )}
          </button>
        </div>

        <SidebarProfileDropdown
          isOpen={isDropdownOpen}
          onClose={() => handleDropdownOpenChange(false)}
          buttonRef={buttonRef}
          user={user}
          quota={quota}
          onOpenIntegrations={onOpenIntegrations}
          onMouseEnter={() => onDropdownHoverChange?.(true)}
          onMouseLeave={() => onDropdownHoverChange?.(false)}
        />
      </>
    )
  }

  return (
    <>
      <div className="border-t border-gray-200/50 p-3">
        {/* Credits display */}
        <AnimatePresence>
          {quota && !hasInfiniteCredits(quota) && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
              className="mb-2 overflow-hidden"
            >
              <div className="bg-ui-hover flex items-center justify-between rounded-lg px-3 py-2">
                <span className="text-ui-secondary text-sm font-medium">Credits</span>
                <span className="text-ui-text text-sm font-semibold">
                  {animatedCredits.toLocaleString()}
                </span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* User profile button */}
        <motion.button
          ref={buttonRef}
          onClick={() => handleDropdownOpenChange(!isDropdownOpen)}
          className="hover:bg-ui-hover flex w-full items-center gap-3 rounded-lg p-2 transition-colors"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-full border border-gray-200">
            {user.image ? (
              <Image
                src={user.image}
                alt={user.name || user.email || 'User'}
                width={40}
                height={40}
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#48039E] to-[#7F039E] text-sm font-medium text-white">
                {userInitial}
              </div>
            )}
          </div>
          <AnimatePresence>
            {!isCollapsed && (
              <motion.div
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 'auto' }}
                exit={{ opacity: 0, width: 0 }}
                transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                className="min-w-0 flex-1 overflow-hidden text-left"
              >
                <div className="text-ui-text truncate text-sm font-medium">
                  {user.name || user.email?.split('@')[0]}
                </div>
                <div className="text-ui-secondary truncate text-xs">
                  {quota?.subscription.name || 'Free'}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.button>
      </div>

      <SidebarProfileDropdown
        isOpen={isDropdownOpen}
        onClose={() => handleDropdownOpenChange(false)}
        buttonRef={buttonRef}
        user={user}
        quota={quota}
        onOpenIntegrations={onOpenIntegrations}
        onMouseEnter={() => onDropdownHoverChange?.(true)}
        onMouseLeave={() => onDropdownHoverChange?.(false)}
      />
    </>
  )
})
